import type { CABTag } from '../types/cabTags';

// Matches CAB ({{tag:...}}) and Build-mode ticket ({{ticket:...}}) / epic
// ({{epic:...}}) reference tokens so message rendering highlights each as a chip.
// Group 1 = kind ("tag" | "ticket" | "epic"), group 2 = path/id, group 3 = label.
// Character classes exclude braces to match the backend grammar
// (backend/services/cab/cab_edit_service.py, backend/services/ticket_service.py).
export const TAG_PATTERN = /\{\{(tag|ticket|epic):([^|{}]+)\|([^{}]+)\}\}/g;

/**
 * Replace every occurrence of `search` in `content` with `replacement`,
 * treating both as literal strings (no regex or `$`-pattern interpretation).
 */
function replaceAllLiteral(content: string, search: string, replacement: string): string {
  return content.split(search).join(replacement);
}

/**
 * Replace every `@<label>` marker in `content` with the `{{…}}` token produced
 * by `toToken(tag)`. Shared by the CAB-tag and ticket/epic serializers, which
 * differ only in how the token string is built.
 */
function serializeMarkers<T extends { label: string }>(
  content: string,
  tags: T[],
  toToken: (tag: T) => string,
): string {
  if (!tags.length) return content;

  let result = content;
  for (const tag of tags) {
    result = replaceAllLiteral(result, `@${tag.label}`, toToken(tag));
  }
  return result;
}

export function serializeTagsIntoContent(content: string, tags: CABTag[]): string {
  return serializeMarkers(
    content,
    tags,
    (tag) => `{{tag:${tag.jsonPath.join('.')}|${tag.label}}}`,
  );
}

/**
 * Build-mode ticket / epic @-reference chip. References tickets by their
 * sequential id (e.g. "TKT-001") and epics by their Mongo id.
 * `type` mirrors ParsedTag's `kind` for the ticket/epic variants.
 */
export interface TicketTag {
  id: string;
  label: string;
  /** Present for ticket references (e.g. "TKT-001"). */
  ticketId?: string;
  /** 'ticket' (default) or 'epic'. Aligned with ParsedTag['kind']. */
  type?: Extract<ParsedTag['kind'], 'ticket' | 'epic'>;
  /** Present for epic references (the epic's Mongo id). */
  epicId?: string;
}

/**
 * Replace each `@<label>` occurrence with its `{{ticket:id|label}}` or
 * `{{epic:id|label}}` token. The backend resolves these into a readable
 * label + a context block for the build agent.
 */
export function serializeTicketTagsIntoContent(content: string, tags: TicketTag[]): string {
  return serializeMarkers(content, tags, (tag) =>
    tag.type === 'epic'
      ? `{{epic:${tag.epicId || tag.id}|${tag.label}}}`
      : `{{ticket:${tag.ticketId || tag.id}|${tag.label}}}`,
  );
}

export interface ParsedTag {
  id: string;
  label: string;
  jsonPath: string[];
  // "tag" = CAB reference, "ticket"/"epic" = Build-mode references.
  kind: 'tag' | 'ticket' | 'epic';
  // For ticket references, the ticket sequential id (e.g. "TKT-014").
  ticketId?: string;
  // For epic references, the epic's Mongo id.
  epicId?: string;
  startIndex: number;
  endIndex: number;
}

export interface ParsedContent {
  tags: ParsedTag[];
  segments: Array<{ type: 'text' | 'tag'; content: string; tag?: ParsedTag }>;
}

export function parseTagsFromContent(content: string): ParsedContent {
  const tags: ParsedTag[] = [];
  const segments: ParsedContent['segments'] = [];
  
  let lastIndex = 0;
  let match;
  const regex = new RegExp(TAG_PATTERN.source, 'g');

  while ((match = regex.exec(content)) !== null) {
    const [fullMatch, kindStr, jsonPathStr, label] = match;
    const startIndex = match.index;
    const endIndex = startIndex + fullMatch.length;

    if (startIndex > lastIndex) {
      segments.push({
        type: 'text',
        content: content.slice(lastIndex, startIndex),
      });
    }

    const kind: ParsedTag['kind'] =
      kindStr === 'ticket' ? 'ticket' : kindStr === 'epic' ? 'epic' : 'tag';
    const jsonPath = jsonPathStr.split('.');
    const tag: ParsedTag = {
      id: `${jsonPathStr}-${label}`,
      label,
      jsonPath,
      kind,
      ticketId: kind === 'ticket' ? jsonPathStr : undefined,
      epicId: kind === 'epic' ? jsonPathStr : undefined,
      startIndex,
      endIndex,
    };

    tags.push(tag);
    segments.push({
      type: 'tag',
      content: label,
      tag,
    });

    lastIndex = endIndex;
  }

  if (lastIndex < content.length) {
    segments.push({
      type: 'text',
      content: content.slice(lastIndex),
    });
  }

  return { tags, segments };
}

export function hasSerializedTags(content: string): boolean {
  return new RegExp(TAG_PATTERN.source).test(content);
}

export function stripTagMarkers(content: string): string {
  return content.replace(TAG_PATTERN, '@$3');
}
